import { writable, derived, get } from 'svelte/store';
import { browser } from '$app/environment';

export interface CookiePreferences {
	necessary: boolean;
	analytics: boolean;
	marketing: boolean;
	preferences: boolean;
}

export interface CookieConsent {
	given: boolean;
	timestamp: number;
	preferences: CookiePreferences;
}

const COOKIE_CONSENT_KEY = 'heyweek_cookie_consent';
const COOKIE_CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000; // 1 year

// Default preferences
const defaultPreferences: CookiePreferences = {
	necessary: true, // Always enabled
	analytics: false,
	marketing: false,
	preferences: false
};

const defaultConsent: CookieConsent = {
	given: false,
	timestamp: 0,
	preferences: defaultPreferences
};

// Load saved consent from localStorage
function loadConsent(): CookieConsent {
	if (!browser) return defaultConsent;
	
	try {
		const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
		if (stored) {
			const consent = JSON.parse(stored) as CookieConsent;
			// Check if consent is still valid (not older than 1 year)
			if (Date.now() - consent.timestamp < COOKIE_CONSENT_DURATION) {
				return consent;
			}
		}
	} catch (e) {
		console.error('Failed to load cookie consent:', e);
	}
	
	return defaultConsent;
}

// Check consent synchronously on page load to prevent flash
export function getInitialConsent(): boolean {
	if (!browser) return true; // Assume consent given on server to prevent flash
	
	try {
		const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
		if (stored) {
			const consent = JSON.parse(stored) as CookieConsent;
			if (Date.now() - consent.timestamp < COOKIE_CONSENT_DURATION) {
				return consent.given;
			}
		}
	} catch (e) {
		// Silent fail
	}
	
	return false;
}

// Save consent to localStorage
function saveConsent(consent: CookieConsent) {
	if (!browser) return;
	
	try {
		localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
	} catch (e) {
		console.error('Failed to save cookie consent:', e);
	}
}

// Create the store
export const cookieConsent = writable<CookieConsent>(loadConsent());

// Subscribe to changes and save
cookieConsent.subscribe((consent) => {
	saveConsent(consent);
});

// Derived store for checking if banner should be shown
export const showCookieBanner = derived(
	cookieConsent,
	($cookieConsent) => !$cookieConsent.given
);

// Helper functions
export function acceptAllCookies() {
	cookieConsent.set({
		given: true,
		timestamp: Date.now(),
		preferences: {
			necessary: true,
			analytics: true,
			marketing: true,
			preferences: true
		}
	});
	applyCookiePreferences();
}

export function acceptNecessaryOnly() {
	cookieConsent.set({
		given: true,
		timestamp: Date.now(),
		preferences: defaultPreferences
	});
	applyCookiePreferences();
}

export function updateCookiePreferences(preferences: Partial<CookiePreferences>) {
	cookieConsent.update((consent) => ({
		...consent,
		given: true,
		timestamp: Date.now(),
		preferences: {
			...consent.preferences,
			...preferences,
			necessary: true // Always keep necessary cookies enabled
		}
	}));
	applyCookiePreferences();
}

export function resetCookieConsent() {
	cookieConsent.set(defaultConsent);
	if (browser) {
		localStorage.removeItem(COOKIE_CONSENT_KEY);
	}
}

// Show cookie preferences (opens the banner)
export function showCookiePreferences() {
	// Force the banner to show by setting the entire consent object
	cookieConsent.set({
		given: false,
		timestamp: Date.now(),
		preferences: get(cookieConsent).preferences
	});
}

// Apply cookie preferences (enable/disable tracking scripts)
function applyCookiePreferences() {
	if (!browser) return;
	
	cookieConsent.subscribe((consent) => {
		// Handle Google Analytics
		if (consent.preferences.analytics && typeof window !== 'undefined') {
			// Enable GA if not already loaded
			if (!(window as any).gtag) {
				loadGoogleAnalytics();
			}
		} else {
			// Disable GA tracking
			if (typeof (window as any).gtag !== 'undefined') {
				(window as any).gtag('consent', 'update', {
					analytics_storage: 'denied'
				});
			}
		}
		
		// Handle marketing cookies (e.g., Facebook Pixel, etc.)
		if (consent.preferences.marketing) {
			// Enable marketing tracking
			loadMarketingScripts();
		} else {
			// Disable marketing tracking
			disableMarketingScripts();
		}
	})();
}

// Load Google Analytics
function loadGoogleAnalytics() {
	const GA_ID = 'YOUR_GA_ID'; // Replace with actual GA ID
	
	// Only load in production
	if (import.meta.env.PROD && GA_ID && GA_ID !== 'YOUR_GA_ID') {
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
		document.head.appendChild(script);
		
		(window as any).dataLayer = (window as any).dataLayer || [];
		function gtag(...args: any[]) {
			(window as any).dataLayer.push(args);
		}
		(window as any).gtag = gtag;
		gtag('js', new Date());
		gtag('config', GA_ID);
		gtag('consent', 'update', {
			analytics_storage: 'granted'
		});
	}
}

// Load marketing scripts
function loadMarketingScripts() {
	// Add Facebook Pixel, LinkedIn Insight Tag, etc.
	// Only load in production
	if (import.meta.env.PROD) {
		// Example: Facebook Pixel
		// loadFacebookPixel();
	}
}

// Disable marketing scripts
function disableMarketingScripts() {
	// Remove or disable marketing cookies
	// Clear marketing-related cookies
	if (browser) {
		// Remove specific marketing cookies
		document.cookie.split(";").forEach((c) => {
			const eqPos = c.indexOf("=");
			const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
			// Check if it's a marketing cookie (customize based on your needs)
			if (name.startsWith('_fb') || name.startsWith('_li')) {
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
			}
		});
	}
}

// Initialize on load
if (browser) {
	applyCookiePreferences();
}